import { useEffect, useRef, useState } from 'react'
import './DonationInitiativeSection.css'
import { FEATURES, PANEL_STATS } from './donationData'
import aboutImg from '../../../assets/images/about_2.png'
function FeatureIcon({ feature }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {feature.path && <path d={feature.path} />}
      {feature.circle && <circle cx={feature.circle.cx} cy={feature.circle.cy} r={feature.circle.r} />}
      {feature.polyline && <polyline points={feature.polyline.join(',')} />}
      {feature.extraPaths?.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}

export default function DonationInitiativeSection() {
  const sectionRef = useRef(null)
  const [entered, setEntered] = useState(false)
  const [barWidth, setBarWidth] = useState('0%')
  const [imgFailed, setImgFailed] = useState(false)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setEntered(true)
        window.setTimeout(() => setBarWidth('70%'), 400)
        observer.disconnect()
      },
      { threshold: 0.08 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <section id="donation-initiative" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.03 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dn-dots-pattern" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#B45309" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dn-dots-pattern)" />
        </svg>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 60% at 0% 50%,rgba(217,119,6,0.07) 0%,transparent 65%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 40% 50% at 100% 0%,rgba(180,83,9,0.06) 0%,transparent 60%)' }} />
        <div style={{ position: 'absolute', top: '-90px', right: '-90px', width: '440px', height: '440px', opacity: 0.055 }}>
          <svg viewBox="0 0 440 440" xmlns="http://www.w3.org/2000/svg">
            <circle cx="220" cy="220" r="200" fill="none" stroke="#B45309" strokeWidth="1" />
            <circle cx="220" cy="220" r="156" fill="none" stroke="#D97706" strokeWidth="0.7" />
            <circle cx="220" cy="220" r="112" fill="none" stroke="#F59E0B" strokeWidth="0.5" strokeDasharray="4 6" />
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: '-50px', left: '-50px', width: '260px', height: '260px', opacity: 0.04 }}>
          <svg viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg">
            <circle cx="130" cy="130" r="115" fill="none" stroke="#B45309" strokeWidth="1" />
          </svg>
        </div>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(217,119,6,0.32),transparent)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(217,119,6,0.22),transparent)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div id="dn-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14" style={{ alignItems: 'center' }}>
          <div className={`dn-al${entered ? ' dn-on' : ''}`} style={{ animationDelay: '.06s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <span style={{ height: '1px', width: '28px', background: 'rgba(180,83,9,0.5)', display: 'block', flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--font-heading),serif', fontSize: '9.5px', fontWeight: 700, letterSpacing: '.28em', color: '#B45309', textTransform: 'uppercase' }}>
                Our Initiative · Education
              </span>
              <span style={{ height: '1px', width: '28px', background: 'rgba(180,83,9,0.5)', display: 'block', flexShrink: 0 }} />
            </div>

            <h2 className="dn-heading">
              Support Education.
              <br />
              <em>Change Lives.</em>
            </h2>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ height: '1px', width: '44px', background: 'linear-gradient(90deg,rgba(217,119,6,0.4),transparent)', display: 'block' }} />
              <span style={{ fontSize: '18px', color: '#D97706', opacity: 0.65, lineHeight: 1 }}>ॐ</span>
              <span style={{ height: '1px', width: '44px', background: 'linear-gradient(90deg,transparent,rgba(217,119,6,0.4))', display: 'block' }} />
            </div>

            <p className="dn-intro">
              An initiative by Astrologer Raju Maharaj Ji — giving back through the wisdom of the stars, one child at
              a time.
            </p>

            <ul className="dn-features">
              {FEATURES.map((feature) => (
                <li className="dn-feature" key={feature.title}>
                  <div className="dn-feature-icon">
                    <FeatureIcon feature={feature} />
                  </div>
                  <div>
                    <div className="dn-feature-title">{feature.title}</div>
                    <div className="dn-feature-sub">{feature.sub}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className={`dn-progress-wrap dn-au${entered ? ' dn-on' : ''}`} style={{ animationDelay: '.24s' }}>
              <div className="dn-progress-header">
                <span className="dn-progress-label">Annual Goal Progress</span>
                <span className="dn-progress-pct">70%</span>
              </div>
              <div className="dn-bar-track">
                <div className="dn-bar-fill" style={{ width: barWidth }} />
              </div>
              <div className="dn-progress-footer">
                <span className="dn-progress-raised">
                  <strong>₹8.4L</strong> raised
                </span>
                <span className="dn-progress-goal">Goal: ₹12L</span>
              </div>
            </div>

            <div className="dn-cta-row">
              <a
                // href=""
                className="dn-cta"
              >
                Donate Now
                <svg className="dn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <a
                // href=""
                className="dn-link"
              >
                Learn About Our Mission
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4M9.5 2.5V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>

          <div className={`dn-ar${entered ? ' dn-on' : ''}`} style={{ animationDelay: '.14s' }}>
            <div className="dn-panel">
              <div className="dn-stats">
                {PANEL_STATS.map((stat) => (
                  <div className="dn-stat" key={stat.label}>
                    <span className="dn-stat-num">{stat.num}</span>
                    <span className="dn-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>

              <div className="dn-panel-img">
                {!imgFailed && (
                  <img
                    src={aboutImg}
                    alt=""
                    aria-hidden="true"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                    onError={() => setImgFailed(true)}
                  />
                )}
                {imgFailed && (
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(145deg,#2A1408,#120804)' }} />
                )}
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,3,0,0.28)' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', background: 'linear-gradient(0deg,rgba(8,3,0,0.75) 0%,transparent 100%)' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 50% at 70% 20%,rgba(232,119,15,0.16) 0%,transparent 60%)' }} />
                <svg style={{ position: 'absolute', top: '-55px', right: '-55px', width: '240px', height: '240px', opacity: 0.13 }} viewBox="0 0 240 240" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="120" cy="120" r="105" fill="none" stroke="#F59E0B" strokeWidth="1" />
                  <circle cx="120" cy="120" r="74" fill="none" stroke="#D97706" strokeWidth="0.7" />
                </svg>
              </div>

              <div className="dn-quote">
                <div className="dn-quote-inner">
                  <svg className="dn-quote-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" />
                    <path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" />
                  </svg>
                  <p className="dn-quote-text">
                    &quot;Education is the most powerful remedy we can offer to the next generation. When a child
                    learns, an entire family transforms.&quot;
                  </p>
                  <div className="dn-quote-author">
                    <div className="dn-quote-avatar">R</div>
                    <div>
                      <div className="dn-quote-name">Raju Maharaj Ji</div>
                      <div className="dn-quote-role">Vedic Astrologer &amp; Founder</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
